import { CircleAlertIcon, CircleCheckIcon, InfoIcon, Loader2Icon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, type CSSProperties } from 'react';
import { Toaster as Sonner, type ToasterProps } from 'sonner';
import { create } from 'zustand';

import { useMediaQuery } from '@/shared/lib/hooks/useMediaQuery';
import { useResolvedTheme } from '@/shared/theme/store';

export { toast } from 'sonner';

/** How many mounted bottom bars (the messenger composer) ask toasts to stay above them. */
const useBottomBars = create<{ count: number }>(() => ({ count: 0 }));

/** Bottom offset of toasts on wide screens while a bottom bar is on screen: above a one-line composer and its Send. */
const RAISED_OFFSET = 104;

/**
 * Keeps toasts off a bar at the bottom of the screen (a chat composer) while the calling component is mounted:
 * on wide screens they rise above it (on phones toasts are at the top anyway).
 */
export function useRaisedToasts(active = true): void {
  useEffect(() => {
    if (!active) return;
    useBottomBars.setState((s) => ({ count: s.count + 1 }));
    return () => useBottomBars.setState((s) => ({ count: s.count - 1 }));
  }, [active]);
}

/**
 * Global toast host (sonner) themed with Orbita tokens. Mounted once by the app providers. Bottom right on wide
 * screens; on phones at the top, where they cover neither a composer nor the page's bottom actions.
 */
export function Toaster(props: ToasterProps) {
  const theme = useResolvedTheme();
  const phone = useMediaQuery('(max-width: 639px)');
  const raised = useBottomBars((s) => s.count > 0);
  return (
    <Sonner
      theme={theme}
      position={phone ? 'top-center' : 'bottom-right'}
      offset={raised && !phone ? { bottom: RAISED_OFFSET } : undefined}
      closeButton
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4 text-success-text" />,
        info: <InfoIcon className="size-4 text-info-text" />,
        warning: <TriangleAlertIcon className="size-4 text-warning-text" />,
        error: <CircleAlertIcon className="size-4 text-destructive-text" />,
        loading: <Loader2Icon className="size-4 animate-spin text-muted-foreground" />,
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius-lg)',
        } as CSSProperties
      }
      toastOptions={{ classNames: { toast: 'shadow-lg! font-sans!', description: 'text-muted-foreground!' } }}
      {...props}
    />
  );
}
